import React, { useMemo, useState } from 'react';
import { createAttempt, submitReading } from '../../api/exams';
import { setPartProgress, computeMistakes, scoreReadingPart } from './_progress';
import { T } from './_theme';
import {
  ReadingFrame, PartTopBar, OptionBtn, FeedbackCard, PrimaryBtn, GhostBtn,
  NudgeBanner, readArabicPref, readingHomeHref,
} from './_primitives';

// FET Reading — Part 1 · Signs & Notices
//
// Content shape:
//   { signs: [{
//       from, to, body, signoff,
//       question, options: [{letter,text}, ...], correct,
//       why_en, why_ar, mistake_en, mistake_ar, evidence,
//       examples: [{good|bad, text}]
//   }, ...] }
// Submission key per sign: sign_{i} → letter

export default function Part1Screen({
  isMobile, navigate, exam, loading,
  onContinueToNextPart, nextPartLabel,
}) {
  const part = useMemo(
    () => (exam?.reading_parts || []).find((p) => p.part_number === 1),
    [exam],
  );
  const signs = useMemo(
    () => (part?.content?.signs || []).filter(Boolean),
    [part],
  );
  const total = signs.length;

  const [arabicOn, setArabicOn] = useState(readArabicPref);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // { sign_0: 'A', ... }
  const [submitting, setSubmitting] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState('');

  const persistArabic = (next) => {
    setArabicOn(next);
    try { window.localStorage.setItem('fet_reading_arabic', next ? '1' : '0'); } catch {}
  };

  if (loading || !exam) {
    return <ReadingFrame isMobile={isMobile}><div style={{ padding: 32, color: T.ink2, fontFamily: T.sans }}>Loading reading exam…</div></ReadingFrame>;
  }
  if (!part || !signs.length) {
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={1} partTitle="Signs & Notices" currentIdx={0} total={1} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24, color: T.ink2, fontFamily: T.sans, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: T.ink }}>No signs configured for Part 1</div>
          <div>An admin needs to add at least one sign to Part 1 from the admin panel.</div>
        </div>
      </ReadingFrame>
    );
  }

  // Result screen — shown after the user submits the last question of the part.
  if (finalResult) {
    const pct = finalResult.max ? Math.round((finalResult.score / finalResult.max) * 100) : 0;
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={1} partTitle="Signs & Notices" currentIdx={total - 1} total={total} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.hairline}`, borderRadius: 18, padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: T.ink3, marginBottom: 12 }}>Part 1 result</div>
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 48, fontWeight: 700, color: T.blue, fontVariantNumeric: 'tabular-nums' }}>{finalResult.score}</span>
              <span style={{ fontSize: 20, color: T.ink3, fontWeight: 600 }}>/ {finalResult.max}</span>
            </div>
            <div style={{ fontSize: 14, color: T.ink2, marginTop: 8 }}>You scored <strong style={{ color: T.ink }}>{pct}%</strong>.</div>
            <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <GhostBtn onClick={() => { setAnswers({}); setCurrentIdx(0); setFinalResult(null); setError(''); }}>Try again</GhostBtn>
              {onContinueToNextPart ? (
                <PrimaryBtn full={false} onClick={onContinueToNextPart}>{nextPartLabel || 'Next part →'}</PrimaryBtn>
              ) : (
                <PrimaryBtn full={false} onClick={() => navigate('/dashboard')}>Back to dashboard</PrimaryBtn>
              )}
            </div>
          </div>
        </div>
      </ReadingFrame>
    );
  }

  const sign = signs[currentIdx] || {};
  const optionList = sign.options || [];
  const correctLetter = String(sign.correct || sign.answer || '').toUpperCase();
  const pickedLetter = answers[`sign_${currentIdx}`] || null;
  const answered = !!pickedLetter;
  const isCorrect = answered && pickedLetter === correctLetter;
  const isLast = currentIdx === total - 1;

  const pickAnswer = (letter) => {
    if (answered) return;
    setAnswers((prev) => ({ ...prev, [`sign_${currentIdx}`]: letter }));
  };

  const goNext = async () => {
    if (!isLast) {
      setCurrentIdx((i) => Math.min(total - 1, i + 1));
      return;
    }
    setSubmitting(true);
    setError('');
    // Score client-side from the canonical content shape — the production
    // backend's old scoring code returns 0 for the new fields, so we
    // compute the real score here. Backend createAttempt + submitReading
    // are still called for persistence, but their score is ignored.
    const local = scoreReadingPart(1, part?.content, answers);
    const finalScore = {
      score: local.score,
      max: local.max || total,
      mistakes: computeMistakes(1, part?.content, answers),
    };
    setFinalResult(finalScore);
    setPartProgress(1, finalScore);
    try {
      const attempt = await createAttempt(exam.id, 'practice', 'reading');
      await submitReading(attempt.id, { '1': answers });
    } catch (err) {
      // Backend persist failed (e.g. no plan) — keep the local score, show
      // an inline note so the student knows their progress lives on this
      // device only.
      const apiErr = err?.response?.data;
      setError(apiErr?.error || 'Could not save your attempt to the server. Your local score is shown.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderSignBody = () => (
    <div style={{ background: T.surface2, border: `2px dashed ${T.hairlineInk}`, borderRadius: 14, padding: 16, marginTop: 14 }}>
      {(sign.from || sign.to) && (
        <div style={{ fontSize: 11, color: T.ink3, marginBottom: 8, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', fontFamily: T.sans }}>
          Note · {sign.from ? `from ${sign.from}` : ''}{sign.from && sign.to ? ' to ' : ''}{sign.to ? sign.to : ''}
        </div>
      )}
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, fontFamily: T.serif, color: T.ink }}>{sign.body || sign.text || '—'}</p>
      {sign.signoff && (
        <div style={{ marginTop: 14, fontSize: 15, fontFamily: T.serif, color: T.ink }}>— {sign.signoff}</div>
      )}
    </div>
  );

  return (
    <ReadingFrame isMobile={isMobile}>
      <PartTopBar partNumber={1} partTitle="Signs & Notices" currentIdx={currentIdx} total={total} onBack={() => navigate(readingHomeHref())} />
      <div style={{ flex: 1, padding: 16 }}>
        <div style={{ fontSize: 14, color: T.ink2, marginBottom: 4 }}>Read the note and choose the correct meaning.</div>
        {renderSignBody()}
        <div style={{ marginTop: 18, fontSize: 15, fontWeight: 600, color: T.ink }}>{sign.question || 'What does this sign / notice mean?'}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {optionList.map((opt) => {
            const letter = String(opt.letter || '').toUpperCase();
            const text = typeof opt === 'string' ? opt : (opt.text || '');
            let state = 'idle';
            if (answered) {
              if (letter === correctLetter) state = 'correct';
              else if (letter === pickedLetter) state = 'incorrect';
              else state = 'dim';
            }
            return (
              <OptionBtn key={letter} letter={letter} state={state} onClick={() => pickAnswer(letter)}>
                {text}
              </OptionBtn>
            );
          })}
        </div>

        {answered && (
          <FeedbackCard
            correct={isCorrect}
            header={isCorrect ? `Nice — ${correctLetter} is correct` : `Almost — the answer is ${correctLetter}`}
            picked={pickedLetter}
            lang={arabicOn ? 'ar' : 'en'}
            onLangChange={(v) => persistArabic(v === 'ar')}
            simple={arabicOn ? sign.why_ar : sign.why_en}
            mistake={!isCorrect ? (arabicOn ? sign.mistake_ar : sign.mistake_en) : null}
            examples={Array.isArray(sign.examples) ? sign.examples : null}
            evidence={sign.evidence}
          />
        )}

        {error && <div style={{ marginTop: 12, fontSize: 13, color: T.miss }}>{error}</div>}

        {!answered && <div style={{ marginTop: 14 }}><NudgeBanner>Pick an answer above to continue to the {isLast ? 'next part' : 'next question'}.</NudgeBanner></div>}
      </div>

      <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.hairline}`, background: T.surface, display: 'flex', gap: 10 }}>
        <GhostBtn disabled={currentIdx === 0} onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}>← Previous</GhostBtn>
        <div style={{ flex: 1 }} />
        <PrimaryBtn full={false} disabled={!answered || submitting} onClick={goNext}>
          {submitting ? 'Saving…' : isLast ? 'Finish part →' : 'Next question →'}
        </PrimaryBtn>
      </div>
    </ReadingFrame>
  );
}
